import {FormattedMessage, useIntl} from "react-intl";
import {Button, Modal, Typography} from "antd";
import React, {useCallback, useContext, useEffect, useState} from "react";
import "../../assets/css/Dentobook.css";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {
    Organization,
    SubscriptionResponse, SubscriptionStatus, MonetaryType, PaymentStatus
} from "../../api-client";
import '../../assets/css/Subscription.css';
import {AlertContext} from "../../context/AlertContext";
import dayjs from "dayjs";
import {getOrganizationApi, getStripeApi} from "../../api/apiInstances";
import {SubscriptionState} from "../../constants/subscriptionStates";
import logger from "../../utils/logger";
import {tokenService} from "../../services/tokenService";

const {Title, Text} = Typography;

const SubscriptionPlan = () => {
    const organizationApi = getOrganizationApi();
    const stripeApi = getStripeApi();
    const {principal, setSelectSubscription} = useContext(DentobookApplicationContext);


    const [, setOrganization] = useState<Organization | undefined>();
    const [subscription, setSubscription] = useState<SubscriptionResponse | undefined>();
    const [nextPaymentDate, setNextPaymentDate] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const {setVisible, setWarning} = useContext(AlertContext);
    const intl = useIntl();

    const fetchOrganization = useCallback(async () => {
        const orgId = tokenService.getOrganizationIdFromToken();
        if (!orgId) return;
        try {
            setLoading(true);
            const response = await organizationApi.fetchOrganizationById(orgId);
            setOrganization(response.data);
            setSubscription(response.data.subscription ?? undefined);

            if (response.data.paymentStatus === PaymentStatus.Failed) {
                setWarning(intl.formatMessage({id: "subscription_payment_failed"}));
                setVisible(true);
            }
        } catch (error) {
            logger.error("Failed to load organization:", error);
        } finally {
            setLoading(false);
        }
    }, [organizationApi, intl, setVisible, setWarning]);

    const fetchNextPayment = async () => {
        const orgId = tokenService.getOrganizationIdFromToken();
        if (!orgId) return;

        const res = await stripeApi.fetchNextPaymentDate(orgId);
        setNextPaymentDate(res.data.nextPaymentDate?.toString() || '');
    };

    useEffect(() => {
        fetchOrganization()
            .then(fetchNextPayment)
            .catch(err => {
                logger.log(err);
            });
    }, []);

    const refreshPage = async () => {
        await fetchOrganization();
        window.location.reload();
    };

    const handleCancel = async () => {
        const orgId = tokenService.getOrganizationIdFromToken();
        if (!orgId) {
            return;
        }

        try {
            setLoading(true);
            await stripeApi.cancelSubscription(orgId);
            await refreshPage();
        } catch (error) {
            logger.error("Failed to cancel subscription:", error);
        }
    };

    const handleRenew = async () => {
        const orgId = tokenService.getOrganizationIdFromToken();
        if (!orgId) {
            return;
        }

        try {
            setLoading(true);
            await stripeApi.renewSubscription(orgId);
            await refreshPage();
        } catch (error) {
            logger.error("Failed to renew subscription:", error);
        }
    };

    const handleSubscribe = async () => {
        setSelectSubscription(SubscriptionState.SHOW_PLAN_SELECTION);
    };

    if (loading) {
        return <h1><FormattedMessage id="loading"/></h1>;
    }

    return (
        <MainInformation
            loading={loading}
            nextPayment={nextPaymentDate}
            subscription={subscription}
            handleCancel={handleCancel}
            handleRenew={handleRenew}
            handleSubscribe={handleSubscribe}
        />
    );
};

const MainInformation = ({
                             organization,
                             loading,
                             subscription,
                             nextPayment,
                             handleCancel,
                             handleRenew,
                             handleSubscribe,
                         }: {
    organization?: Organization;
    loading: boolean;
    subscription?: SubscriptionResponse;
    handleCancel: () => void;
    handleRenew: () => void;
    handleSubscribe: () => void;
    nextPayment: string;
}) => {
    const isActive = subscription?.status === SubscriptionStatus.Active;
    const isFreeTrial = subscription?.plan?.monetaryType === MonetaryType.FreeTrial;
    const [isCancelModalVisible, setIsCancelModalVisible] = useState<boolean>(false);

    let actionButton;
    const intl = useIntl();
    const getSubscriptionDescription = () => {
        switch (subscription?.plan?.monetaryType) {
            case MonetaryType.FreeTrial:
                return <FormattedMessage id="subscription_description_free_trial_profile" />;
            case MonetaryType.Paid:
                return <FormattedMessage id="dentobook_pro"/>;
            case MonetaryType.Free:
                return <FormattedMessage id="subscription_description_start"/>;
            default:
                return <FormattedMessage id="subscription_description"/>;
        }
    };


    if (isActive) {
        if (isFreeTrial || subscription.plan?.monetaryType === MonetaryType.Free) {
            // Free trial subscriptions allow subscribing to a paid plan.
            actionButton = (
                <Button className="calendar-form-btn-secondary-subscription" onClick={handleSubscribe}>
                    <FormattedMessage id="subscribe"/>
                </Button>
            );
        } else {
            // Active subscriptions (not free trials) allow cancellation.
            actionButton = (
                <Button className="calendar-form-btn-secondary-subscription"
                        onClick={() => setIsCancelModalVisible(true)}>
                    <FormattedMessage id="cancel_subscription"/>
                </Button>
            );
        }
    } else {
        actionButton = (
            <Button className="calendar-form-btn-secondary-subscription" onClick={handleRenew} disabled={loading}>
                <FormattedMessage id="renew_subscription"/>
            </Button>
        );
    }

    return (
        <div>
            <Title level={4}>
                <FormattedMessage id="active_subscription"/>
            </Title>
            <div style={{ marginTop: '2rem' }}>
            <span className="body-large-medium">
                {isFreeTrial ? (
                    <FormattedMessage id="free_trial_plan_name" defaultMessage="Free Trial Plan" />
                ) : (
                    subscription?.plan?.name || 'N/A'
                )}
            </span>
            </div>
            <p>
                <span className="body-large-regular">{getSubscriptionDescription()}</span>
            </p>
            {subscription?.plan?.monetaryType !== MonetaryType.Free &&
                subscription?.plan?.monetaryType !== MonetaryType.FreeTrial && (
                <div style={{marginTop: "2rem"}}>
                    <p>
            <span className="body-large-medium">
                <FormattedMessage id={isActive ? "next_payment" : "expires_at"}/>:
            </span>
                    </p>
                    <p>
                        <Text>
                            {nextPayment ?? "Not Available"}
                        </Text>
                    </p>
                </div>
            )}
            {subscription?.plan?.monetaryType === MonetaryType.FreeTrial && (
                <div style={{marginTop: "2rem"}}>
                    <p>
            <span className="body-large-medium">
                <FormattedMessage id="subscription_expiration"/>
            </span>
                    </p>
                    <p>
                        <Text>
                            {dayjs(subscription.expiration).format('DD.MM.YYYY')}
                        </Text>
                    </p>
                </div>
            )}
            <div style={{marginTop: "2rem"}}>{actionButton}</div>
            <Modal
                transitionName=""
                okButtonProps={{
                    className: "modal-btn"
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                confirmLoading={loading}
                className="dentobook-modal"
                okText={intl.formatMessage({id: 'save'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
                open={isCancelModalVisible} onOk={handleCancel}
                onCancel={() => setIsCancelModalVisible(false)}
            >{<p
                style={{
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: '500',
                    marginBottom: '0'
                }}
            >
                <FormattedMessage id="cancel_subscription_text"/>
            </p>}
            </Modal>
        </div>
    );
};

export default SubscriptionPlan;